import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '../../lib/api/client'
import type { SchoolRetention, SiteRetention } from '../../lib/api/client'
import { Button, Field, Input, Pill, Spinner } from '../../components/ui'
import { Icon } from '../../components/icons'
import { toast } from '../../components/toast'
import './retention.css'

export function SiteRetentionSettings() {
  const queryClient = useQueryClient()
  const key = ['retention', 'site']
  const policy = useQuery({ queryKey: key, queryFn: api.siteRetention })
  const save = useMutation({
    mutationFn: api.updateSiteRetention,
    onSuccess: (updated) => {
      queryClient.setQueryData(key, updated)
      queryClient.invalidateQueries({ queryKey: ['retention', 'school'] })
      toast('Site archive limit saved.')
    },
    onError: (error: Error) => toast(error.message, 'error'),
  })

  if (policy.isLoading) return <Spinner label="Loading archive retention" />
  if (!policy.data) return null

  return (
    <SiteRetentionForm
      key={policy.data.max_days}
      policy={policy.data}
      saving={save.isPending}
      onSave={(days) => save.mutate(days)}
    />
  )
}

function SiteRetentionForm({
  policy,
  saving,
  onSave,
}: {
  policy: SiteRetention
  saving: boolean
  onSave: (days: number) => void
}) {
  const [days, setDays] = useState(String(policy.max_days))
  const parsed = Number(days)
  const valid = Number.isInteger(parsed) && parsed >= 1 && parsed <= policy.absolute_max_days

  return (
    <RetentionPanel
      eyebrow="Platform policy"
      title="Archive retention ceiling"
      description="Set the longest period any school may keep an archived paper before permanent deletion."
      badge={`Maximum ${policy.absolute_max_days} days`}
    >
      <Field
        label="Maximum retention period"
        hint="Lowering this value also lowers any school setting that is currently above it."
      >
        <div className="tc-retention-control">
          <Input
            type="number"
            min={1}
            max={policy.absolute_max_days}
            value={days}
            onChange={(event) => setDays(event.target.value)}
          />
          <span>days</span>
        </div>
      </Field>
      <RetentionSummary days={valid ? parsed : policy.max_days} />
      <div className="tc-retention-actions">
        <Button
          variant="primary"
          disabled={!valid || parsed === policy.max_days || saving}
          onClick={() => onSave(parsed)}
        >
          {saving ? 'Saving…' : 'Save site limit'}
        </Button>
      </div>
    </RetentionPanel>
  )
}

export function SchoolRetentionSettings({ schoolId }: { schoolId?: string }) {
  const queryClient = useQueryClient()
  const scope = schoolId ?? 'current'
  const key = ['retention', 'school', scope]
  const policy = useQuery({
    queryKey: key,
    queryFn: () => api.schoolRetention(schoolId),
  })
  const save = useMutation({
    mutationFn: (days: number) => api.updateSchoolRetention(days, schoolId),
    onSuccess: (updated) => {
      queryClient.setQueryData(key, updated)
      toast('School archive retention saved.')
    },
    onError: (error: Error) => toast(error.message, 'error'),
  })

  if (policy.isLoading) return <Spinner label="Loading archive retention" />
  if (!policy.data) return null

  return (
    <SchoolRetentionForm
      key={`${policy.data.retention_days}-${policy.data.site_max_days}`}
      policy={policy.data}
      saving={save.isPending}
      onSave={(days) => save.mutate(days)}
    />
  )
}

function SchoolRetentionForm({
  policy,
  saving,
  onSave,
}: {
  policy: SchoolRetention
  saving: boolean
  onSave: (days: number) => void
}) {
  const [days, setDays] = useState(String(policy.retention_days))
  const parsed = Number(days)
  const valid = Number.isInteger(parsed) && parsed >= 1 && parsed <= policy.site_max_days

  return (
    <RetentionPanel
      eyebrow="School policy"
      title="Archived paper retention"
      description="Choose how long this school's papers remain recoverable after a teacher archives them."
      badge={`Site limit ${policy.site_max_days} days`}
    >
      <Field
        label="Retention period"
        hint={`Choose between 1 and ${policy.site_max_days} days. The site administrator controls this ceiling.`}
      >
        <div className="tc-retention-control">
          <Input
            type="number"
            min={1}
            max={policy.site_max_days}
            value={days}
            onChange={(event) => setDays(event.target.value)}
          />
          <span>days</span>
        </div>
      </Field>
      <RetentionSummary days={valid ? parsed : policy.retention_days} />
      <div className="tc-retention-actions">
        <Button
          variant="primary"
          disabled={!valid || parsed === policy.retention_days || saving}
          onClick={() => onSave(parsed)}
        >
          {saving ? 'Saving…' : 'Save retention period'}
        </Button>
      </div>
    </RetentionPanel>
  )
}

function RetentionPanel({
  eyebrow,
  title,
  description,
  badge,
  children,
}: {
  eyebrow: string
  title: string
  description: string
  badge: string
  children: React.ReactNode
}) {
  return (
    <section className="tc-retention-panel">
      <header className="tc-retention-heading">
        <span className="tc-retention-icon" aria-hidden="true">
          <Icon.archive size={20} />
        </span>
        <div>
          <span className="tc-retention-eyebrow">{eyebrow}</span>
          <h2>{title}</h2>
          <p>{description}</p>
        </div>
        <Pill tone="secondary">{badge}</Pill>
      </header>
      <div className="tc-retention-body">{children}</div>
    </section>
  )
}

function RetentionSummary({ days }: { days: number }) {
  return (
    <div className="tc-retention-summary">
      <Icon.warning size={17} />
      <span>
        A paper moved to Archive remains restorable for <strong>{days} days</strong>, then it is
        permanently deleted.
      </span>
    </div>
  )
}
